/**
 * HW countdown — live timer for [data-hw-countdown].
 *
 * Modes:
 * - fixed_end:          Count down to data-end ISO timestamp (original behaviour)
 * - fake_urgency:       Looping timer using data-duration (seconds); resets on expiry
 * - next_day_delivery:  Counts to data-end (today's cutoff); on expiry swaps to
 *                       data-next-target / data-next-delivery-day and recalculates
 *
 * Split markup: .hw-countdown__prefix + .hw-countdown__timer + .hw-countdown__suffix
 * Supports {countdown}, {time}, and {deliveryDay} placeholders in data-text-template.
 * Legacy: single .hw-countdown__output text node.
 */

(function () {
  'use strict';

  const SELECTOR = '[data-hw-countdown], [data-hw-announcement-countdown]';

  /**
   * @param {HTMLElement} el
   * @returns {HTMLElement | null}
   */
  function findLegacyOutput(el) {
    if (el.querySelector('.hw-countdown__timer')) {
      return null;
    }

    return el.querySelector(
      '.hw-countdown__output, .hw-countdown__value, .hw-announcement-countdown__value',
    );
  }

  /**
   * @param {string} template
   * @param {string} token
   * @param {string} [deliveryDay]
   * @returns {string}
   */
  function applyTextTemplate(template, token, deliveryDay) {
    const raw = (template || '').trim();
    const t = token || '';
    if (!raw) {
      return t;
    }

    const hasPlaceholder = /\{countdown\}|\{time\}/i.test(raw);
    let out = raw
      .replace(/\{countdown\}/gi, t)
      .replace(/\{time\}/gi, t);

    if (deliveryDay) {
      out = out.replace(/\{deliveryDay\}/gi, deliveryDay);
    }

    if (!hasPlaceholder && t) {
      out = `${raw} ${t}`;
    }

    return out;
  }

  /**
   * @param {HTMLElement} el
   * @returns {string}
   */
  function readTemplateFromEl(el) {
    return el.getAttribute('data-text-template') || '';
  }

  /**
   * Update the {deliveryDay} spans in prefix/suffix elements.
   * @param {HTMLElement} el
   * @param {string} dayName
   */
  function updateDeliveryDaySpans(el, dayName) {
    const spans = el.querySelectorAll('.hw-countdown__delivery-day');
    for (const span of spans) {
      span.textContent = dayName;
    }
  }

  /**
   * @param {HTMLElement} el
   * @param {string} token - Timer value or static date / expired message
   * @returns {boolean}
   */
  function setSplitParts(el, token) {
    const prefixEl = el.querySelector('.hw-countdown__prefix');
    const timerEl = el.querySelector('.hw-countdown__timer');
    const suffixEl = el.querySelector('.hw-countdown__suffix');
    const template = readTemplateFromEl(el);

    if (!timerEl) {
      return false;
    }

    if (!template.trim()) {
      timerEl.textContent = token;
      return true;
    }

    const hasPh = /\{countdown\}|\{time\}/i.test(template);
    if (hasPh) {
      const parts = template.split(/\{countdown\}|\{time\}/i);
      const prefix = (parts[0] || '').replace(/\{deliveryDay\}/gi, '');
      const suffix = (parts[1] || '').replace(/\{deliveryDay\}/gi, '');
      if (prefixEl) {
        // Don't overwrite innerHTML for NDD mode — delivery day spans are managed separately
        if (!el.querySelector('.hw-countdown__delivery-day')) {
          prefixEl.textContent = prefix;
        }
      }
      if (suffixEl) {
        if (!el.querySelector('.hw-countdown__delivery-day')) {
          suffixEl.textContent = suffix;
        }
      }
      timerEl.textContent = token;
      return true;
    }

    if (prefixEl) {
      if (!el.querySelector('.hw-countdown__delivery-day')) {
        prefixEl.textContent = `${template.trim()} `;
      }
    }
    if (suffixEl) {
      suffixEl.textContent = '';
    }
    timerEl.textContent = token;
    return true;
  }

  /**
   * @param {number} totalMs
   * @returns {string}
   */
  function formatRemaining(totalMs) {
    if (totalMs <= 0) {
      return '';
    }

    const totalSec = Math.floor(totalMs / 1000);
    const pad = (n) => String(n).padStart(2, '0');

    const days = Math.floor(totalSec / 86400);
    const remAfterDays = totalSec % 86400;
    const h = Math.floor(remAfterDays / 3600);
    const m = Math.floor((remAfterDays % 3600) / 60);
    const s = remAfterDays % 60;
    const clock = `${pad(h)}:${pad(m)}:${pad(s)}`;

    if (days > 0) {
      return `${days}d ${clock}`;
    }

    if (h > 0) {
      return clock;
    }

    return `${pad(m)}:${pad(s)}`;
  }

  /**
   * @param {string} iso
   * @returns {number | null}
   */
  function parseEnd(iso) {
    const t = Date.parse(iso);
    if (Number.isNaN(t)) {
      return null;
    }

    return t;
  }

  /**
   * Calculate the next NDD cutoff from the client's perspective.
   * Used when the server-rendered "next" values are also expired (e.g. page was open overnight).
   * @param {number} cutoffHour
   * @param {number} cutoffMinute
   * @param {boolean} excludeWeekends
   * @returns {{ targetMs: number, deliveryDay: string }}
   */
  function calcClientNdd(cutoffHour, cutoffMinute, excludeWeekends) {
    const now = new Date();
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate(), cutoffHour, cutoffMinute, 0, 0);

    let targetMs;
    let deliveryDate;

    if (now.getTime() < today.getTime()) {
      targetMs = today.getTime();
      deliveryDate = new Date(today);
      deliveryDate.setDate(deliveryDate.getDate() + 1);
    } else {
      const tomorrow = new Date(today);
      tomorrow.setDate(tomorrow.getDate() + 1);
      targetMs = tomorrow.getTime();
      deliveryDate = new Date(tomorrow);
      deliveryDate.setDate(deliveryDate.getDate() + 1);
    }

    if (excludeWeekends) {
      // Skip weekend for the target itself
      const targetDate = new Date(targetMs);
      const tDow = targetDate.getDay();
      if (tDow === 6) {
        targetDate.setDate(targetDate.getDate() + 2);
        targetMs = targetDate.getTime();
        deliveryDate = new Date(targetDate);
        deliveryDate.setDate(deliveryDate.getDate() + 1);
      } else if (tDow === 0) {
        targetDate.setDate(targetDate.getDate() + 1);
        targetMs = targetDate.getTime();
        deliveryDate = new Date(targetDate);
        deliveryDate.setDate(deliveryDate.getDate() + 1);
      }

      // Skip weekend for delivery
      const dDow = deliveryDate.getDay();
      if (dDow === 6) {
        deliveryDate.setDate(deliveryDate.getDate() + 2);
      } else if (dDow === 0) {
        deliveryDate.setDate(deliveryDate.getDate() + 1);
      }
    }

    return {
      targetMs,
      deliveryDayIndex: deliveryDate.getDay(),
    };
  }

  /**
   * @param {HTMLElement} el
   * @param {HTMLElement | null} legacyEl
   */
  function applyExpired(el, legacyEl) {
    const hide = el.getAttribute('data-hide-when-expired') === 'true';
    const msg = (el.getAttribute('data-expired-message') || '').trim();
    const template = readTemplateFromEl(el);

    if (hide) {
      el.setAttribute('hidden', '');
      el.classList.add('hw-countdown--hidden');
      el.classList.add('hw-announcement-countdown--hidden');
      return;
    }

    if (msg) {
      if (setSplitParts(el, msg)) {
        return;
      }
      if (legacyEl) {
        legacyEl.textContent = applyTextTemplate(template, msg);
      }
      return;
    }

    if (setSplitParts(el, '')) {
      return;
    }

    if (legacyEl) {
      legacyEl.textContent = '';
    }
  }

  /**
   * Initialise a fixed_end countdown (original behaviour).
   * @param {HTMLElement} el
   */
  function initFixedEnd(el) {
    const raw = el.getAttribute('data-end');
    if (!raw) {
      return;
    }

    const endMs = parseEnd(raw);
    const legacyEl = findLegacyOutput(el);
    const template = readTemplateFromEl(el);

    const reduceMotion = window.matchMedia(
      '(prefers-reduced-motion: reduce)',
    ).matches;

    if (endMs === null) {
      if (setSplitParts(el, '—')) {
        return;
      }
      if (legacyEl) {
        legacyEl.textContent = applyTextTemplate(template, '—');
      }
      return;
    }

    if (reduceMotion) {
      const d = new Date(endMs);
      const dateStr = d.toLocaleString(undefined, {
        dateStyle: 'medium',
        timeStyle: 'short',
      });
      if (setSplitParts(el, dateStr)) {
        return;
      }
      if (legacyEl) {
        legacyEl.textContent = applyTextTemplate(template, dateStr);
      }
      return;
    }

    let intervalId;

    const tick = () => {
      if (!el.isConnected) {
        if (intervalId !== undefined) {
          window.clearInterval(intervalId);
          intervalId = undefined;
        }
        activeTicks.delete(tick);
        return;
      }

      const remaining = endMs - Date.now();
      if (remaining <= 0) {
        if (intervalId !== undefined) {
          window.clearInterval(intervalId);
          intervalId = undefined;
        }
        applyExpired(el, legacyEl);
        activeTicks.delete(tick);
        return;
      }

      const formatted = formatRemaining(remaining);
      if (setSplitParts(el, formatted)) {
        return;
      }
      if (legacyEl) {
        legacyEl.textContent = applyTextTemplate(template, formatted);
      }
    };

    tick();
    intervalId = window.setInterval(tick, 1000);
    activeTicks.add(tick);
  }

  /**
   * Initialise a fake_urgency countdown — loops on expiry.
   * @param {HTMLElement} el
   */
  function initFakeUrgency(el) {
    const durationSecs = parseInt(el.getAttribute('data-duration') || '0', 10);
    if (durationSecs <= 0) {
      return;
    }

    const durationMs = durationSecs * 1000;

    // Seed from sessionStorage so refreshes don't reset the timer
    const storageKey = `hw-urgency-${el.closest('[id]')?.id || 'global'}`;
    let endMs;

    const stored = sessionStorage.getItem(storageKey);
    if (stored) {
      const parsed = parseInt(stored, 10);
      if (!Number.isNaN(parsed) && parsed > Date.now()) {
        endMs = parsed;
      }
    }

    if (!endMs) {
      endMs = Date.now() + durationMs;
      try {
        sessionStorage.setItem(storageKey, String(endMs));
      } catch (_) {
        // sessionStorage may be unavailable
      }
    }

    let intervalId;

    const tick = () => {
      if (!el.isConnected) {
        if (intervalId !== undefined) {
          window.clearInterval(intervalId);
          intervalId = undefined;
        }
        activeTicks.delete(tick);
        return;
      }

      let remaining = endMs - Date.now();

      if (remaining <= 0) {
        // Reset the loop
        endMs = Date.now() + durationMs;
        try {
          sessionStorage.setItem(storageKey, String(endMs));
        } catch (_) {
          // noop
        }
        remaining = durationMs;
      }

      const formatted = formatRemaining(remaining);
      setSplitParts(el, formatted);
    };

    tick();
    intervalId = window.setInterval(tick, 1000);
    activeTicks.add(tick);
  }

  /**
   * Initialise a next_day_delivery countdown.
   * @param {HTMLElement} el
   */
  function initNdd(el) {
    let endMs = parseEnd(el.getAttribute('data-end') || '');
    let deliveryDay = el.getAttribute('data-delivery-day') || '';
    let nextTargetMs = parseEnd(el.getAttribute('data-next-target') || '');
    let nextDeliveryDay = el.getAttribute('data-next-delivery-day') || '';

    // Timezone offset in minutes (merchant's local time vs UTC)
    const tzOffsetMin = parseInt(el.getAttribute('data-ndd-tz-offset') || '0', 10) || 0;

    // Locale-aware day names from Liquid (Sun–Sat, matching JS getDay() order)
    const dayNamesAttr = el.getAttribute('data-day-names') || '';
    const dayNames = dayNamesAttr.split(',').map(s => s.trim());
    const fallbackDayNames = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
    const getDayName = (idx) => (dayNames.length === 7 ? dayNames[idx] : fallbackDayNames[idx]);

    // Extract cutoff hour/minute from the target ISO, adjusting for tz offset
    // The server stored UTC = localHour - tzOffset, so reverse it
    const targetDate = endMs ? new Date(endMs) : null;
    const cutoffHourUTC = targetDate ? targetDate.getUTCHours() : 14;
    const cutoffMinuteUTC = targetDate ? targetDate.getUTCMinutes() : 0;
    // Convert back to local for client-side recalc
    const cutoffHour = cutoffHourUTC + Math.floor(tzOffsetMin / 60);
    const cutoffMinute = cutoffMinuteUTC + (tzOffsetMin % 60);
    const excludeWeekends = el.getAttribute('data-ndd-exclude-weekends') !== 'false';

    if (!endMs) {
      // Server didn't provide a target — calculate client-side
      const calc = calcClientNdd(cutoffHour, cutoffMinute, excludeWeekends);
      endMs = calc.targetMs;
      deliveryDay = getDayName(calc.deliveryDayIndex);
    }

    // Set the initial delivery day in the DOM
    updateDeliveryDaySpans(el, deliveryDay);

    let intervalId;

    const tick = () => {
      if (!el.isConnected) {
        if (intervalId !== undefined) {
          window.clearInterval(intervalId);
          intervalId = undefined;
        }
        activeTicks.delete(tick);
        return;
      }

      const remaining = endMs - Date.now();

      if (remaining <= 0) {
        // Current cutoff has passed — try next target
        if (nextTargetMs && nextTargetMs > Date.now()) {
          endMs = nextTargetMs;
          deliveryDay = nextDeliveryDay;
          nextTargetMs = null;
          nextDeliveryDay = '';
          updateDeliveryDaySpans(el, deliveryDay);
        } else {
          // Both server targets expired — recalculate client-side
          const calc = calcClientNdd(cutoffHour, cutoffMinute, excludeWeekends);
          endMs = calc.targetMs;
          deliveryDay = getDayName(calc.deliveryDayIndex);
          updateDeliveryDaySpans(el, deliveryDay);
        }

        // Re-tick immediately with new values
        const newRemaining = endMs - Date.now();
        if (newRemaining <= 0) {
          // Should not happen, but safety
          setSplitParts(el, '--:--:--');
          return;
        }
        const formatted = formatRemaining(newRemaining);
        setSplitParts(el, formatted);
        return;
      }

      const formatted = formatRemaining(remaining);
      setSplitParts(el, formatted);
    };

    tick();
    intervalId = window.setInterval(tick, 1000);
    activeTicks.add(tick);
  }

  /**
   * @param {HTMLElement} el
   */
  function initOne(el) {
    const mode = el.getAttribute('data-mode') || 'fixed_end';

    switch (mode) {
      case 'fake_urgency':
        initFakeUrgency(el);
        break;
      case 'next_day_delivery':
        initNdd(el);
        break;
      case 'fixed_end':
      default:
        initFixedEnd(el);
        break;
    }
  }

  /**
   * Set of active tick functions — one per running countdown.
   * Shared listener calls each on tab visibility restore.
   * @type {Set<Function>}
   */
  const activeTicks = new Set();

  document.addEventListener('visibilitychange', () => {
    if (!document.hidden) {
      for (const tick of activeTicks) {
        tick();
      }
    }
  });

  function init() {
    for (const el of document.querySelectorAll(SELECTOR)) {
      initOne(el);
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
